/**
 * @fileoverview Authorized Invoice Email Addresses section for Org Settings.
 *
 * v3.0 Phase 3.5 (2026-08-29) — proactive-add authorization model. An
 * org admin/editor manages the mailboxes allowed to push invoices into
 * their org here. Adding a mailbox sends the sender a self-service
 * verify link; only once verified does inbound mail from that address
 * flow into the OCR queue.
 *
 * Renders as a card in OrgSettings between the auto-file toggle and
 * the members card. Uses Seedling design tokens (no inline colors, no
 * hex literals) and re-uses the shared Modal / Toast / ConfirmModal
 * components already in use elsewhere on the page.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import Modal from './Modal';
import Toast from './Toast';
import ConfirmModal from './ConfirmModal';

/** Human-friendly label for a verification_status token. */
const STATUS_LABELS = {
  pending: 'Pending',
  verified: 'Verified',
  expired: 'Expired',
};

/**
 * Format an ISO 8601 timestamp as a locale string, or em-dash on empty.
 */
function fmtTimestamp(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch (_) {
    return iso;
  }
}

/**
 * Render a Seedling status badge for a `verification_status`.
 */
function StatusBadge({ status }) {
  const label = STATUS_LABELS[status] || status || 'Unknown';
  const tone = status === 'verified'
    ? 'badge--success'
    : status === 'expired'
      ? 'badge--error'
      : 'badge--warning';
  return (
    <span
      className={`badge ${tone}`}
      data-testid={`aie-status-${status}`}
    >
      {label}
    </span>
  );
}

StatusBadge.propTypes = {
  status: PropTypes.string,
};

/**
 * Main component.
 *
 * @param {Object} props
 * @param {number} props.orgId - Currently-active org id.
 * @returns {JSX.Element|null}
 */
function AuthorizedInvoiceEmails({ orgId }) {
  const [rows, setRows] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addBusinessId, setAddBusinessId] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [actingId, setActingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const loadRows = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const [{ data: senders }, { data: biz }] = await Promise.all([
        api.listOrgEmailIngestSenders(orgId),
        api.getBusinesses({ include_inactive: false }),
      ]);
      setRows(Array.isArray(senders?.rows) ? senders.rows : []);
      setBusinesses(Array.isArray(biz) ? biz : []);
    } catch (err) {
      setError(
        err?.response?.data?.error
        || 'Failed to load authorized invoice email addresses',
      );
      setRows([]);
      setBusinesses([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const singleBusiness = businesses.length === 1 ? businesses[0] : null;

  // Reset the modal state whenever the org changes or the modal opens.
  useEffect(() => {
    if (addOpen) {
      setAddEmail('');
      setAddError(null);
      // Default the business select to the single-business auto-pick,
      // or to '' (which serializes to null = org-wide binding) when
      // the org has multiple businesses.
      setAddBusinessId(singleBusiness ? String(singleBusiness.id) : '');
    }
  }, [addOpen, singleBusiness]);

  async function handleSubmitAdd(e) {
    e.preventDefault();
    setAddError(null);
    const trimmed = addEmail.trim().toLowerCase();
    if (!trimmed) {
      setAddError('Email is required');
      return;
    }
    setAdding(true);
    try {
      const body = { email: trimmed };
      if (addBusinessId !== '') {
        body.business_id = parseInt(addBusinessId, 10);
      }
      await api.addOrgEmailIngestSender(orgId, body);
      setAddOpen(false);
      setToast({
        type: 'success',
        message: `Verification email sent to ${trimmed}`,
      });
      loadRows();
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'email_already_authorized') {
        setAddError(
          'That email is already authorized on another cogslog account.',
        );
      } else {
        setAddError(
          err?.response?.data?.message
          || err?.response?.data?.error
          || 'Failed to add authorized sender',
        );
      }
    } finally {
      setAdding(false);
    }
  }

  function handleRemove(row) {
    setConfirmAction({
      title: 'Remove Authorized Email',
      message: (
        `Remove ${row.email} from the authorized invoice senders? `
        + 'Future emails from this address will be rejected.'
      ),
      onConfirm: async () => {
        setActingId(row.id);
        try {
          await api.removeOrgEmailIngestSender(orgId, row.id);
          setToast({
            type: 'success',
            message: `Removed ${row.email}`,
          });
          await loadRows();
        } catch (err) {
          setToast({
            type: 'error',
            message: (
              err?.response?.data?.error
              || `Failed to remove ${row.email}`
            ),
          });
        } finally {
          setActingId(null);
          setConfirmAction(null);
        }
      },
    });
  }

  async function handleResend(row) {
    setActingId(row.id);
    try {
      await api.resendOrgEmailIngestVerification(orgId, row.id);
      setToast({
        type: 'success',
        message: `Verification email resent to ${row.email}`,
      });
      await loadRows();
    } catch (err) {
      const code = err?.response?.data?.error;
      setToast({
        type: 'error',
        message: code === 'already_verified'
          ? `${row.email} is already verified`
          : err?.response?.data?.message
            || err?.response?.data?.error
            || `Failed to resend verification to ${row.email}`,
      });
    } finally {
      setActingId(null);
    }
  }

  const sortedRows = useMemo(() => {
    // Newest first — the server already returns created_at DESC but
    // sort defensively so any client-side dedup doesn't reorder.
    return [...rows].sort((a, b) => {
      if (!a.added_at) return 1;
      if (!b.added_at) return -1;
      return new Date(b.added_at).getTime() - new Date(a.added_at).getTime();
    });
  }, [rows]);

  return (
    <div
      className="card org-settings__card"
      data-testid="authorized-invoice-emails"
    >
      <div className="org-settings__section-header">
        <h2
          className="org-settings__section-title"
          style={{ fontSize: '1.15rem' }}
        >
          Authorized invoice email addresses
        </h2>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setAddOpen(true)}
          data-testid="aie-open-add-btn"
        >
          Add email address
        </button>
      </div>
      <p className="hint">
        Only email addresses added and verified here can send invoices
        to invoices@cogslog.com and have them processed for this
        organization. Anything else is rejected with a friendly reply.
      </p>

      {error && (
        <p className="err-msg" role="alert">{error}</p>
      )}

      {loading && (
        <div className="ops-panel__loading" role="status" aria-live="polite">
          Loading authorized emails&hellip;
        </div>
      )}

      {!loading && !error && sortedRows.length === 0 && (
        <div className="empty-state">
          <div className="empty-state__title">No authorized senders</div>
          <p className="empty-state__description">
            Add an email address above and we&rsquo;ll send them a
            verification link. Only verified senders can push invoices
            into your OCR queue.
          </p>
        </div>
      )}

      {!loading && sortedRows.length > 0 && (
        <div className="tbl-wrap">
          <table aria-label="Authorized invoice email addresses">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Status</th>
                <th scope="col">Business</th>
                <th scope="col">Added</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr
                  key={`aie-${row.id}`}
                  data-testid={`aie-row-${row.id}`}
                >
                  <td>{row.email}</td>
                  <td><StatusBadge status={row.verification_status} /></td>
                  <td>
                    {row.business_name || (
                      <span className="admin-cell-muted">
                        Org-wide (pick per invoice)
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="td-amt">
                      {fmtTimestamp(row.added_at)}
                    </span>
                    {row.added_by_display_name && (
                      <span className="hint" style={{ display: 'block' }}>
                        by {row.added_by_display_name}
                      </span>
                    )}
                  </td>
                  <td>
                    {row.verification_status !== 'verified' && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleResend(row)}
                        disabled={actingId === row.id}
                        data-testid={`aie-resend-btn-${row.id}`}
                      >
                        Resend link
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleRemove(row)}
                      disabled={actingId === row.id}
                      aria-label={`Remove ${row.email}`}
                      data-testid={`aie-remove-btn-${row.id}`}
                      style={{ color: 'var(--error)' }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add authorized invoice email"
        size="medium"
      >
        <form onSubmit={handleSubmitAdd}>
          <div className="field">
            <label className="label" htmlFor="aie-email">
              Sender email <span className="req">*</span>
            </label>
            <input
              id="aie-email"
              className="input"
              type="email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              placeholder="ap@vendor.com"
              required
              autoFocus
              data-testid="aie-email-input"
            />
            <span className="hint">
              We&rsquo;ll email a verification link to this address.
              Only after they click it will their invoices be processed.
            </span>
          </div>

          <div className="field">
            <label className="label" htmlFor="aie-business">
              Business
            </label>
            <select
              id="aie-business"
              className="input"
              value={addBusinessId}
              onChange={(e) => setAddBusinessId(e.target.value)}
              disabled={!!singleBusiness}
              data-testid="aie-business-select"
            >
              {singleBusiness ? (
                <option value={String(singleBusiness.id)}>
                  {singleBusiness.name}
                </option>
              ) : (
                <>
                  <option value="">Org-wide (assign per invoice)</option>
                  {businesses.map((b) => (
                    <option key={b.id} value={String(b.id)}>
                      {b.name}
                    </option>
                  ))}
                </>
              )}
            </select>
            <span className="hint">
              {singleBusiness
                ? 'Only business in this org — invoices auto-file here.'
                : 'Pick a business to auto-file invoices from this sender, '
                  + 'or leave as Org-wide to assign each invoice during review.'}
            </span>
          </div>

          {addError && (
            <p className="err-msg" role="alert" data-testid="aie-add-error">
              {addError}
            </p>
          )}

          <div style={{
            display: 'flex',
            gap: 'var(--sp2)',
            marginTop: 'var(--sp4)',
          }}
          >
            <button
              type="submit"
              className="btn btn-primary"
              disabled={adding}
              data-testid="aie-submit-btn"
            >
              {adding ? 'Sending…' : 'Send verification'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {confirmAction && (
        <ConfirmModal
          isOpen
          title={confirmAction.title}
          message={confirmAction.message}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

AuthorizedInvoiceEmails.propTypes = {
  orgId: PropTypes.number,
};

export default AuthorizedInvoiceEmails;
